import { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Button, TextField,
  Dialog, DialogTitle, DialogContent, Link,
} from '@mui/material';
import { MdEdit, MdClose } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Patch } from '../../Axios/AxiosFunctions';
import { BaseURL, apiHeader } from '../../config/apiUrl';

// Meeting transcripts are stored as WEBVTT (cue numbers + timestamps + "Speaker: text").
// Strip the machinery and keep readable "Speaker: text" lines. Phone transcripts are
// already prose, so they pass through unchanged.
export function formatTranscript(raw) {
  if (!raw) return '';
  if (!/WEBVTT/i.test(raw)) return raw;
  return raw
    .replace(/^WEBVTT.*$/im, '')
    .split(/\r?\n\r?\n/)
    .map((block) =>
      block
        .split(/\r?\n/)
        .filter((l) => l && !/^\d+$/.test(l.trim()) && !l.includes('-->'))
        .join(' ')
        .trim(),
    )
    .filter(Boolean)
    .join('\n');
}

/**
 * Read-only Zoom summary with an inline edit pencil + a "View full transcript"
 * link. Editing PATCHes zoom/summary/:id (marks the record `edited` so a later
 * webhook/sync won't overwrite it).
 */
export default function EditableSummary({ summary }) {
  const editorId = useSelector((s) => s?.authReducer?.user?._id || s?.authReducer?.user?.id);
  const accessToken = useSelector((s) => s?.authReducer?.access_token);
  const initial = summary?.zoomSummary || summary?.gptSummary || '';
  const [displayText, setDisplayText] = useState(initial);
  const [draft, setDraft] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    const t = summary?.zoomSummary || summary?.gptSummary || '';
    setDisplayText(t);
    setDraft(t);
  }, [summary?._id, summary?.zoomSummary, summary?.gptSummary]);

  const handleSave = async () => {
    if (!summary?._id) return;
    setSaving(true);
    try {
      await Patch(BaseURL(`zoom/summary/${summary._id}`), { zoomSummary: draft, editedBy: editorId }, apiHeader(accessToken));
      setDisplayText(draft);
      setEditing(false);
      toast.success('Summary updated');
    } catch (e) {
      toast.error('Failed to update summary');
    } finally {
      setSaving(false);
    }
  };

  const verbatim = summary?.verbatimTranscript;
  const transcript = summary?.transcript;
  // Phone calls carry a pre-formatted verbatimTranscript; meetings carry WEBVTT
  // in `transcript`. Older records stuffed summary prose into `transcript` —
  // don't offer those as a "transcript".
  const isVerbatim = !!verbatim || (!!transcript && /WEBVTT|-->/.test(transcript));
  const transcriptText = verbatim || formatTranscript(transcript);

  return (
    <Box mt={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="subtitle2"><b>Summary:</b></Typography>
        {!editing && summary?._id && (
          <IconButton size="small" onClick={() => { setDraft(displayText); setEditing(true); }} title="Edit summary">
            <MdEdit size={16} />
          </IconButton>
        )}
      </Box>
      {editing ? (
        <Box>
          <TextField multiline minRows={4} fullWidth value={draft} onChange={(e) => setDraft(e.target.value)} disabled={saving} />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            <Button size="small" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 'max-content', overflowY: 'auto', background: '#fff', p: 1, borderRadius: 1, border: '1px solid #eee' }}>
          <ReactMarkdown>{displayText}</ReactMarkdown>
        </Box>
      )}

      {isVerbatim && !editing && (
        <Box mt={0.5}>
          <Link component="button" type="button" underline="hover" sx={{ fontSize: '0.8rem' }} onClick={() => setShowTranscript(true)}>
            View full transcript
          </Link>
        </Box>
      )}

      <Dialog open={showTranscript} onClose={() => setShowTranscript(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Full Transcript
          <IconButton size="small" onClick={() => setShowTranscript(false)}><MdClose /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.9rem', m: 0 }}>
            {transcriptText}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
